import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Shell, Wordmark } from "./components/Shell";
import { Button, ErrorNote, Spinner, ToastHost } from "./components/ui";
import { api, type Me } from "./lib/api";
import { boot, completeSignIn, NotFound, signedIn, signIn, signInReady, type SiteInfo } from "./lib/auth";
import AppPlaceholder from "./pages/AppPlaceholder";
import Launcher from "./pages/Launcher";
import Tenants from "./pages/Tenants";
import UsersPage from "./pages/Users";

type State = { step: "loading" } | { step: "notfound" } | { step: "error"; message: string } | { step: "signin"; site: SiteInfo; error?: string } | { step: "ready"; me: Me };

export default function App() {
  const [state, setState] = useState<State>({ step: "loading" });
  useEffect(() => {
    (async () => {
      let site: SiteInfo;
      try {
        site = (await boot()).site;
      } catch (e) {
        return setState(e instanceof NotFound ? { step: "notfound" } : { step: "error", message: (e as Error).message });
      }
      const error = await completeSignIn();
      if (error || !signedIn()) return setState({ step: "signin", site, error: error ?? undefined });
      try {
        setState({ step: "ready", me: await api<Me>("/me") });
      } catch (e) {
        setState({ step: "signin", site, error: (e as Error).message });
      }
    })();
  }, []);

  if (state.step === "loading") return <Spinner />;
  if (state.step === "notfound") return <Centered title="Portal not found">There's no Intellias App Portal at this address. Check the link your administrator sent you.</Centered>;
  if (state.step === "error") return <Centered title="Something went wrong">{state.message}</Centered>;
  if (state.step === "signin") return <SignIn site={state.site} error={state.error} />;

  const me = state.me;
  return (
    <BrowserRouter>
      <Shell me={me}>
        <Routes>
          {me.kind === "platform" ? (
            <Route path="/" element={<Tenants />} />
          ) : (
            <>
              <Route path="/" element={<Launcher me={me} />} />
              {me.tenantAdmin && <Route path="/admin/users" element={<UsersPage me={me} />} />}
              <Route path="/apps/:appId/*" element={<AppPlaceholder me={me} />} />
            </>
          )}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Shell>
      <ToastHost />
    </BrowserRouter>
  );
}

function SignIn({ site, error }: { site: SiteInfo; error?: string }) {
  const title = site.kind === "platform" ? "Platform console" : site.tenant.name;
  return (
    <Centered title={title}>
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        {signInReady() ? (
          <Button variant="primary" className="w-full h-10" onClick={() => void signIn()}>
            {site.kind === "tenant" && site.identityProvider ? "Sign in with your organization" : "Sign in"}
          </Button>
        ) : (
          <p>Single sign-on for {title} is still being set up. Your administrator will let you know when it's ready.</p>
        )}
      </div>
    </Centered>
  );
}

function Centered({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-h-full flex flex-col">
      <div className="brand-bar h-1" aria-hidden />
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-surface border border-line rounded-2xl p-8 shadow-sm">
          <Wordmark className="h-7" />
          <h1 className="text-xl font-bold text-ink mt-6 mb-1">{title}</h1>
          <p className="text-xs text-muted mb-5">Intellias App Portal</p>
          <div className="text-sm text-ink-2">{children}</div>
        </div>
      </div>
      <div className="text-center text-xs text-muted py-4">Powered by Intellias</div>
    </div>
  );
}
